/**
 * @module Movie Template
 *
 * @import {ElementLinkCallback} from "@acng/frontend-stargazer";
 * @import {MovieNodeConfig} from "../../ts/moviePool/template";
 * @import {Movie as Movie_} from "../model/movie";
 * @import {Amateur as Creator} from "../../amateurPool/model/amateur";
 */

import {LocalActor} from '@acng/frontend-relativity';
import {IS, guard} from '@acng/frontend-rubicon';
import {closest} from '@acng/frontend-bounty/dom/query.js';
import {HIDDEN, HREF} from '@acng/frontend-bounty/dom/attribute.js';
import {remove} from '@acng/frontend-bounty/dom/move.js';
import {NodeUpgrade} from '@acng/frontend-stargazer';

import {get as getAmateur} from '../../amateurPool/service/cache.js';
import {CreatorTemplateConfig} from '../../amateurPool/service/template.js';
import {ctxGallery} from '../../core/context/gallery.js';
import {onClick} from '../../layout/service/worker.js';
import {ONCLICK_HREF} from '../../layout/attribute/onclick-href.js';
import {moviePoolFeature} from '../config/feature.js';
import {ctxMovie} from '../context/movie.js';
import {get} from './cache.js';

const provideMovie = LocalActor(ctxMovie);
const provideGallery = LocalActor(ctxGallery);

/**
 * @param {Movie_["id"]} movieId
 *
 * @returns {Promise<ElementLinkCallback<HTMLElement>>}
 */
export const ListItem = async (movieId) => {
  const movie = await get(movieId);
  const amateur = await getAmateur(`${movie.user_id}`);
  const config = await MovieTemplateConfig(movie, amateur);

  /**
   * @type {ElementLinkCallback<HTMLElement>}
   */

  return MovieLink(movie, config);
};

/**
 * Remove the element if the {@link moviePoolFeature} is hidden.
 *
 * > Use this as *stargazer* compile callback.
 *
 * @param {Element} element
 */
export const compile = (element) => {
  if (moviePoolFeature[HIDDEN]) {
    remove(element);
  }
};

/**
 * @param {Movie_} movie
 * @param {Creator | null} creator
 *
 * @returns {Promise<MovieNodeConfig>}
 */
export const MovieTemplateConfig = async (movie, creator) => {
  ASSERT: if (creator && `${movie.user_id}` != creator.id) console.warn('creator mismatch', {movie, creator});

  const realTitle = movie.getTitle();
  const description = movie.getDescription() ?? '';
  const previewSrc = getPreviewSrc(movie);

  return {
    name: realTitle ?? (creator ? `${creator.getNickname()} (${creator.age})` : '-'),
    image: movie.getImageUrl(),
    description,
    url: movie.path,
    hd: movie.isHd(),
    video: !previewSrc ? null : {contentUrl: previewSrc},
    expand:
      previewSrc || (!realTitle && !description) ?
        false
      : {
          heading: !!realTitle,
        },
    creator,
  };
};

/**
 * @param {Movie_} movie
 * @param {Awaited<ReturnType<typeof MovieTemplateConfig>>} config
 *
 * @returns {ElementLinkCallback<HTMLElement>}
 */
export const MovieLink = (movie, config) => (element, engine) => {
  NodeUpgrade(engine, config);
  provideMovie(element, movie);
  provideGallery(element, movie);

  return (element) =>
    onClick(element, (_element, {target}) => {
      ASSERT: guard(target, IS(Element));
      if (!closest(null, target, `[${HREF}],[${ONCLICK_HREF}]`)) {
        location.hash = movie.path;
      }
    });
};

/**
 * @param {Movie_} movie
 *
 * @returns {string | undefined}
 */
const getPreviewSrc = (movie) => {
  /*
  TEST_DATA: {
    DEBUG: console.warn('fake video');
    // Movie preview test data. Three out of five will have a preview video.
    return [
      'https://c2.ng-source.com/175/495/071/u17549571/850805/528723-preview-480x270.mp4',
      'https://c2.ng-source.com/179/557/012/u17955712/878160/538740-preview-480x270.mp4',
      'https://c2.ng-source.com/161/018/041/u16101841/873138/536938-preview-480x270.mp4',
    ][movie.id % 5];
  }
  */

  return movie.movie_previews?.[0];
};
